import {useState, useMemo} from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    InputAdornment,
    Alert,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material"
import {Visibility, VisibilityOff, CheckCircle, RadioButtonUnchecked} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import axiosInstance from "../config/axios-config";
import i18n from "../i18n/i18n";

const ChangePassword = ({open, onClose, userId}) => {
    const {t} = useTranslation()

    const [currentPassword, setCurrentPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState(false)

    const passwordValidation = useMemo(() => {
        return {
            minLength: newPassword.length >= 12,
            hasUpperCase: /[A-Z]/.test(newPassword),
            hasLowerCase: /[a-z]/.test(newPassword),
            hasDigit: /[0-9]/.test(newPassword),
            hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(newPassword),
            passwordsMatch: newPassword === confirmPassword && newPassword !== "" && confirmPassword !== "",
        }
    }, [newPassword, confirmPassword])

    const isPasswordValid = useMemo(() => {
        return Object.values(passwordValidation).every((isValid) => isValid)
    }, [passwordValidation])

    const handleClose = () => {
        setCurrentPassword("")
        setNewPassword("")
        setConfirmPassword("")
        setError("")
        setSuccess(false)
        onClose()
    }

    const validatePassword = () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            setError(t("AllFieldsRequired") || "All fields are required")
            return false
        }

        if (!isPasswordValid) {
            setError(t("PasswordRequirementsNotMet") || "Password does not meet all requirements")
            return false
        }

        return true
    }

    const handleChangePassword = async () => {
        setError("")

        if (!validatePassword()) {
            return
        }

        setLoading(true)
        try {
            const response = await axiosInstance.patch(`/api/v1/users/change-password/${userId}`, {},
                {
                    params: {
                        oldPassword: currentPassword,
                        newPassword: newPassword
                    },
                    headers: {
                        "Language-Context": i18n.language.toUpperCase() || "EN",
                    }
                })

            if (response.data) {
                setSuccess(true)
                setTimeout(() => {
                    handleClose()
                }, 2000)
            } else {
                setError(t("changePasswordFailed") || "Failed to change password")
            }
        } catch (error) {
            console.error("Error changing password:", error)
            setError(error.response?.data?.message || t("changePasswordFailed") || "Failed to change password")
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t("ChangeCurrentPassword") || "Change Password"}</DialogTitle>
            <DialogContent>
                <Box sx={{mt: 1}}>
                    {error && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{mb: 2}}>
                            {t("changePasswordSuccess") || "Password changed successfully!"}
                        </Alert>
                    )}

                    <TextField
                        label={t("CurrentPassword") || "Current Password"}
                        type={showCurrentPassword ? "text" : "password"}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                                        {showCurrentPassword ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label={t("NewPassword") || "New Password"}
                        type={showNewPassword ? "text" : "password"}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                        {showNewPassword ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {newPassword && (
                        <Box sx={{
                            mt: 1,
                            mb: 2,
                            p: 2,
                            bgcolor: "background.paper",
                            borderRadius: 1,
                            border: "1px solid #e0e0e0"
                        }}>
                            <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600}}>
                                {t("PasswordRequirements") || "Password Requirements:"}
                            </Typography>
                            <List dense sx={{py: 0}}>
                                <ListItem sx={{py: 0.5, px: 0}}>
                                    <ListItemIcon sx={{minWidth: 32}}>
                                        {passwordValidation.minLength ? (
                                            <CheckCircle sx={{fontSize: 20, color: "success.main"}}/>
                                        ) : (
                                            <RadioButtonUnchecked sx={{fontSize: 20, color: "text.disabled"}}/>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t("PasswordMinLength") || "At least 12 characters"}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: "0.875rem",
                                                color: passwordValidation.minLength ? "success.main" : "text.secondary",
                                            },
                                        }}
                                    />
                                </ListItem>
                                <ListItem sx={{py: 0.5, px: 0}}>
                                    <ListItemIcon sx={{minWidth: 32}}>
                                        {passwordValidation.hasUpperCase ? (
                                            <CheckCircle sx={{fontSize: 20, color: "success.main"}}/>
                                        ) : (
                                            <RadioButtonUnchecked sx={{fontSize: 20, color: "text.disabled"}}/>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t("PasswordUppercase") || "At least one uppercase letter (A-Z)"}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: "0.875rem",
                                                color: passwordValidation.hasUpperCase ? "success.main" : "text.secondary",
                                            },
                                        }}
                                    />
                                </ListItem>
                                <ListItem sx={{py: 0.5, px: 0}}>
                                    <ListItemIcon sx={{minWidth: 32}}>
                                        {passwordValidation.hasLowerCase ? (
                                            <CheckCircle sx={{fontSize: 20, color: "success.main"}}/>
                                        ) : (
                                            <RadioButtonUnchecked sx={{fontSize: 20, color: "text.disabled"}}/>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t("PasswordLowercase") || "At least one lowercase letter (a-z)"}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: "0.875rem",
                                                color: passwordValidation.hasLowerCase ? "success.main" : "text.secondary",
                                            },
                                        }}
                                    />
                                </ListItem>
                                <ListItem sx={{py: 0.5, px: 0}}>
                                    <ListItemIcon sx={{minWidth: 32}}>
                                        {passwordValidation.hasDigit ? (
                                            <CheckCircle sx={{fontSize: 20, color: "success.main"}}/>
                                        ) : (
                                            <RadioButtonUnchecked sx={{fontSize: 20, color: "text.disabled"}}/>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t("PasswordDigit") || "At least one digit (0-9)"}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: "0.875rem",
                                                color: passwordValidation.hasDigit ? "success.main" : "text.secondary",
                                            },
                                        }}
                                    />
                                </ListItem>
                                <ListItem sx={{py: 0.5, px: 0}}>
                                    <ListItemIcon sx={{minWidth: 32}}>
                                        {passwordValidation.hasSpecialChar ? (
                                            <CheckCircle sx={{fontSize: 20, color: "success.main"}}/>
                                        ) : (
                                            <RadioButtonUnchecked sx={{fontSize: 20, color: "text.disabled"}}/>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t("PasswordSpecialChar") || "At least one special character (!@#$%^&*...)"}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: "0.875rem",
                                                color: passwordValidation.hasSpecialChar ? "success.main" : "text.secondary",
                                            },
                                        }}
                                    />
                                </ListItem>
                            </List>
                        </Box>
                    )}

                    <TextField
                        label={t("ConfirmNewPassword") || "Confirm New Password"}
                        type={showConfirmPassword ? "text" : "password"}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                        {showConfirmPassword ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {confirmPassword && (
                        <Box sx={{mt: 1, display: "flex", alignItems: "center"}}>
                            {passwordValidation.passwordsMatch ? (
                                <>
                                    <CheckCircle sx={{fontSize: 20, color: "success.main", mr: 1}}/>
                                    <Typography variant="body2" sx={{color: "success.main"}}>
                                        {t("PasswordsMatch") || "Passwords match"}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <RadioButtonUnchecked sx={{fontSize: 20, color: "error.main", mr: 1}}/>
                                    <Typography variant="body2" sx={{color: "error.main"}}>
                                        {t("PasswordsDoNotMatch") || "Passwords do not match"}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={handleClose} disabled={loading}>
                    {t("Cancel") || "Cancel"}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleChangePassword}
                    disabled={loading || success || !isPasswordValid}
                >
                    {loading ? t("Changing") || "Changing..." : t("SaveNewPassword") || "Save New Password"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ChangePassword
